import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatCAD, formatDate } from "@/lib/utils-cart";
import { Copy, LinkIcon, Plus } from "lucide-react";

export default function Affiliate() {
  const [links, setLinks] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", store_id: "", campaign_name: "" });

  const load = () => {
    api.get("/affiliate/links").then((r) => setLinks(r.data));
    api.get("/affiliate/stats").then((r) => setStats(r.data));
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/affiliate/links", { ...form, product_id: form.product_id || null, store_id: form.store_id || null });
      setForm({ product_id: "", store_id: "", campaign_name: "" });
      setOpen(false);
      toast.success("Link created");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const copy = (code) => {
    const url = `${window.location.origin}/r/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Copied to clipboard");
  };

  return (
    <DashboardLayout
      title="Affiliate Hub"
      subtitle="Create referral links, track conversions, request payouts."
      actions={<button onClick={() => setOpen(!open)} className="v-btn-primary" data-testid="affiliate-add-link"><Plus size={14} /> Create link</button>}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Links" value={stats?.total_links ?? 0} />
        <Stat label="Clicks" value={stats?.total_clicks ?? 0} />
        <Stat label="Conversions" value={stats?.total_conversions ?? 0} />
        <Stat label="Earnings" value={formatCAD(stats?.total_earnings ?? 0)} />
      </div>

      {open && (
        <form onSubmit={create} className="v-card mb-4 space-y-3">
          <h3 className="font-display font-semibold text-primary">New affiliate link</h3>
          <input className="v-input" placeholder="Campaign name (optional)" value={form.campaign_name} onChange={(e) => setForm({...form, campaign_name: e.target.value})} data-testid="affiliate-link-name" />
          <input className="v-input" placeholder="Product ID (paste from /product/<id>) — optional" value={form.product_id} onChange={(e) => setForm({...form, product_id: e.target.value})} data-testid="affiliate-link-product" />
          <input className="v-input" placeholder="Store ID (optional)" value={form.store_id} onChange={(e) => setForm({...form, store_id: e.target.value})} data-testid="affiliate-link-store" />
          <div className="flex gap-2"><button className="v-btn-primary" data-testid="affiliate-link-save">Create</button><button type="button" onClick={() => setOpen(false)} className="v-btn-outline">Cancel</button></div>
        </form>
      )}

      <div className="v-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="p-3">Link</th>
              <th className="p-3">Campaign</th>
              <th className="p-3 text-right">Clicks</th>
              <th className="p-3 text-right">Conversions</th>
              <th className="p-3 text-right">Earnings</th>
              <th className="p-3">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {links.map((l) => (
              <tr key={l.id} className="border-b border-border last:border-0">
                <td className="p-3 font-mono text-xs"><LinkIcon size={12} className="inline mr-1 text-secondary" /> {l.code}</td>
                <td className="p-3">{l.campaign_name || "—"}</td>
                <td className="p-3 text-right font-mono">{l.clicks}</td>
                <td className="p-3 text-right font-mono">{l.conversions}</td>
                <td className="p-3 text-right font-mono">{formatCAD(l.earnings)}</td>
                <td className="p-3 text-xs">{formatDate(l.created_at)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => copy(l.code)} className="v-btn-outline text-xs" data-testid={`affiliate-copy-${l.code}`}>
                    <Copy size={12} /> Copy URL
                  </button>
                </td>
              </tr>
            ))}
            {links.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No links yet. Create your first one to start earning.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="v-card">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display font-black text-2xl text-primary mt-1">{value}</div>
    </div>
  );
}
